import { createSlice } from '@reduxjs/toolkit';
import { getDefaultParticipantsCount, getDefaultWinnersCount } from '../../utils/participantLimits';

const initialState = {
    id: 0,
    title: "",
    participantsCount: 0,
    prizePlacesCount: 0,
    totalAmount: 0,
    currentAmount: 0,
    status: "PENDING",
    winnersCount: 0,
    durationDays: 0,
    tokenType: "",
    creatorUserId: 1,
    link: "Promoter link",
    maxParticipantsCount: 0,
    category: {
        category_name: "",
        positions: [],
    },
    promoType: "crystalsAndCrypto",
    promoters: [],
    startDate: null,
    
    // Дополнительные поля для формы
    selectedBlockchain: null,
    foundToken: null,
    selectedCrystalCategory: null,
    promoTime: null,
    tasks: [],
    
    // Состояние отправки
    isSubmitting: false,
    submitError: null,
    submitSuccess: false,
    
    // Бонусная позиция (1st Time Free)
    bonusPosition: null,

    // Ручное распределение winners по местам (null = авто по win %)
    customPlaceWinners: null,
    // Ручное распределение budget по местам (null = авто по budget_percent)
    customPlaceBudgets: null,
};

const promoFormSlice = createSlice({
    name: 'promoForm',
    initialState,
    reducers: {
        // Основные поля формы
        setTitle: (state, action) => {
            state.title = action.payload;
        },
        
        setParticipantsCount: (state, action) => {
            state.participantsCount = action.payload;
        },
        
        setPrizePlacesCount: (state, action) => {
            state.prizePlacesCount = action.payload;
            state.customPlaceWinners = null;
        },

        setCustomPlaceWinners: (state, action) => {
            state.customPlaceWinners = action.payload;
        },

        setCustomPlaceBudgets: (state, action) => {
            state.customPlaceBudgets = action.payload;
        },
        
        setTotalAmount: (state, action) => {
            state.totalAmount = action.payload;
        },
        
        setDurationDays: (state, action) => {
            state.durationDays = action.payload;
        },
        
        setTokenType: (state, action) => {
            state.tokenType = action.payload;
        },
        
        setLink: (state, action) => {
            state.link = action.payload;
        },
        
        setMaxParticipantsCount: (state, action) => {
            state.maxParticipantsCount = action.payload;
        },
        
        setCategory: (state, action) => {
            state.category = action.payload;
        },
        
        setPromoType: (state, action) => {
            state.promoType = action.payload;
        },
        
        setStartDate: (state, action) => {
            state.startDate = action.payload;
        },
        
        // Дополнительные поля
        setSelectedBlockchain: (state, action) => {
            state.selectedBlockchain = action.payload;
        },
        
        setFoundToken: (state, action) => {
            state.foundToken = action.payload;
        },
        
        setSelectedCrystalCategory: (state, action) => {
            state.selectedCrystalCategory = action.payload;
        },
        
        setPromoTime: (state, action) => {
            state.promoTime = action.payload;
        },
        
        setTasks: (state, action) => {
            state.tasks = action.payload;
        },
        
        // Состояние отправки
        setSubmitting: (state, action) => {
            state.isSubmitting = action.payload;
        },
        
        setSubmitError: (state, action) => {
            state.submitError = action.payload;
        },
        
        setSubmitSuccess: (state, action) => {
            state.submitSuccess = action.payload;
        },
        
        // Бонусная позиция (1st Time Free)
        setBonusPosition: (state, action) => {
            state.bonusPosition = action.payload;
        },
        
        // Синхронизация с другими slices
        syncWithBudget: (state, action) => {
            const { budget, selectedBlockchain, foundToken } = action.payload;
            if (state.totalAmount !== budget) {
                state.customPlaceBudgets = null;
            }
            state.totalAmount = budget;
            state.selectedBlockchain = selectedBlockchain;
            state.foundToken = foundToken;
        },
        
        syncWithCrystals: (state, action) => {
            const { selectedId, crystals } = action.payload;
            const selectedCrystal = crystals.find(c => c.id === selectedId);
            if (selectedCrystal) {
                const prevId = state.selectedCrystalCategory?.id ?? null;
                const categoryChanged = prevId !== selectedCrystal.id;
                state.selectedCrystalCategory = selectedCrystal;
                if (categoryChanged) {
                    // Reset defaults only when switching categories, or on first pick with empty counts.
                    // Preserve edit-hydrated participants/winners when category is first attached.
                    const switchingCategories = prevId != null;
                    const emptyCounts = !(state.participantsCount > 0) && !(state.prizePlacesCount > 0);
                    if (switchingCategories || emptyCounts) {
                        state.participantsCount = getDefaultParticipantsCount(selectedCrystal);
                        state.prizePlacesCount = getDefaultWinnersCount(selectedCrystal);
                        state.maxParticipantsCount = getDefaultParticipantsCount(selectedCrystal);
                        state.customPlaceWinners = null;
                        state.customPlaceBudgets = null;
                    } else if (!(state.maxParticipantsCount > 0)) {
                        state.maxParticipantsCount = state.participantsCount;
                    }
                }
            }
        },
        
        syncWithPromoTime: (state, action) => {
            const { selectedDay } = action.payload;
            state.durationDays = selectedDay;
        },
        
        // Сброс формы
        resetForm: (state) => {
            return { ...initialState };
        },
        
        // Загрузка существующего промо для редактирования
        loadPromo: (state, action) => {
            const promo = action.payload;
            return { ...state, ...promo };
        }
    }
});

// Экспортируем actions
export const {
    setTitle,
    setParticipantsCount,
    setPrizePlacesCount,
    setCustomPlaceWinners,
    setCustomPlaceBudgets,
    setTotalAmount,
    setDurationDays,
    setTokenType,
    setLink,
    setMaxParticipantsCount,
    setCategory,
    setPromoType,
    setStartDate,
    setSelectedBlockchain,
    setFoundToken,
    setSelectedCrystalCategory,
    setPromoTime,
    setTasks,
    setSubmitting,
    setSubmitError,
    setSubmitSuccess,
    setBonusPosition,
    syncWithBudget,
    syncWithCrystals,
    syncWithPromoTime,
    resetForm,
    loadPromo
} = promoFormSlice.actions;

// Селекторы
export const selectPromoForm = (state) => state.promoForm;
export const selectFormData = (state) => {
    const form = state.promoForm;
    return {
        title: form.title,
        participantsCount: form.participantsCount,
        prizePlacesCount: form.prizePlacesCount,
        totalAmount: form.totalAmount,
        durationDays: form.durationDays,
        tokenType: form.tokenType,
        link: form.link,
        maxParticipantsCount: form.maxParticipantsCount,
        category: form.category,
        promoType: form.promoType,
        startDate: form.startDate,
        selectedBlockchain: form.selectedBlockchain,
        foundToken: form.foundToken,
        selectedCrystalCategory: form.selectedCrystalCategory,
        promoTime: form.promoTime,
        tasks: form.tasks
    };
};
export const selectIsSubmitting = (state) => state.promoForm.isSubmitting;
export const selectSubmitError = (state) => state.promoForm.submitError;
export const selectSubmitSuccess = (state) => state.promoForm.submitSuccess;

export default promoFormSlice.reducer;